import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useSpring, useReducedMotion } from "framer-motion";
import { Reveal } from "../common/Reveal";
import { Img } from "../common/Img";
import { milestoneService, resolveImageUrl } from "../../services/catalogService";
import { useLanguage } from "../../context/LanguageContext";

// Client observations (27-oct): interactive About Us that tells TESSA's
// milestones — the company turns 30 in 2027. Content is admin-managed
// (Admin → About) in EN/RU; other languages fall back to English.
export default function AboutTimeline() {
  const { t, localize } = useLanguage();
  const reduce = useReducedMotion();
  const [milestones, setMilestones] = useState(null);
  const listRef = useRef(null);

  // Scroll-linked progress line
  const { scrollYProgress } = useScroll({
    target: listRef,
    offset: ["start 0.75", "end 0.6"],
  });
  const progress = useSpring(scrollYProgress, { stiffness: 90, damping: 24 });

  useEffect(() => {
    let alive = true;
    milestoneService.list().then((items) => { if (alive) setMilestones(items); }).catch(() => alive && setMilestones([]));
    return () => { alive = false; };
  }, []);

  if (milestones && milestones.length === 0) return null;

  return (
    <section id="about" className="bg-[#FFFFFF] py-24 md:py-32 overflow-hidden" data-testid="about-section">
      <div className="container-tessa">
        <Reveal className="max-w-3xl mb-16 md:mb-24">
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47] block">
            {t("about.eyebrow")}
          </span>
          <h2 className="mt-4 font-serif text-4xl leading-[1.0] text-[#252525] md:text-5xl lg:text-6xl">
            {t("about.title1")}{" "}
            <span className="italic text-[#D92A47]">{t("about.title2")}</span>
          </h2>
          <p className="mt-5 text-[#817B74] leading-relaxed max-w-2xl">{t("about.subtitle")}</p>
        </Reveal>

        <div ref={listRef} className="relative">
          {/* Base line + scroll progress line */}
          <div className="absolute left-[7px] md:left-1/2 top-0 bottom-0 w-px bg-black/10 md:-translate-x-1/2" aria-hidden="true" />
          <motion.div
            className="absolute left-[7px] md:left-1/2 top-0 bottom-0 w-px bg-[#D92A47] origin-top md:-translate-x-1/2"
            style={{ scaleY: reduce ? 1 : progress }}
            aria-hidden="true"
          />

          <ol className="space-y-14 md:space-y-20">
            {(milestones || []).map((m, i) => {
              const leftSide = i % 2 === 0;
              return (
                <motion.li
                  key={m.id}
                  initial={{ opacity: 0, y: reduce ? 0 : 36 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="relative pl-10 md:pl-0 md:grid md:grid-cols-2 md:gap-16"
                  data-testid={`milestone-${m.year}`}
                >
                  {/* Node on the line */}
                  <span
                    className="absolute left-0 md:left-1/2 top-2 h-[15px] w-[15px] rounded-full border-2 border-[#D92A47] bg-white md:-translate-x-1/2"
                    aria-hidden="true"
                  />
                  <div className={leftSide ? "md:col-start-1 md:text-right md:pr-4" : "md:col-start-2 md:pl-4"}>
                    <div className="font-serif text-5xl md:text-6xl leading-none text-[#D92A47]/90">{m.year}</div>
                    <h3 className="mt-3 font-serif text-2xl md:text-3xl text-[#252525]">
                      {localize(m, "title")}
                    </h3>
                    <p className={`mt-3 text-[#817B74] leading-relaxed max-w-md ${leftSide ? "md:ml-auto" : ""}`}>
                      {localize(m, "text")}
                    </p>
                    {m.image && (
                      <div className={`mt-5 overflow-hidden ${leftSide ? "md:ml-auto" : ""} max-w-md`}>
                        <Img
                          src={resolveImageUrl(m.image)}
                          alt={localize(m, "title")}
                          className="aspect-[16/10] w-full object-cover transition-transform duration-700 hover:scale-105"
                        />
                      </div>
                    )}
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
